/**
 * Club-house (§ 7) : occupation anonymisée, demande de réservation avec
 * signalement de conflits, validation par le comité (conflit bloquant).
 */
import React, { useState } from 'react';
import { api, uuid } from '../lib/api.js';
import { CalendarView } from '../components/Calendar.jsx';
import { Spinner, ErrorBox, Badge, useApi, Intro, IconeCorbeille } from '../components/common.jsx';
import { fmtDateHeure, capitaliser } from '../lib/format.js';

export function Clubhouse({ me, toast }) {
  const [lieux] = useApi('lieux', { cache: 'lieux' });
  const [lieuId, setLieuId] = useState(null);
  const [mesResas, reloadResas] = useApi('reservations', { deps: [] });

  if (lieux.loading) return <Spinner />;
  if (!lieux.ok) return <ErrorBox message={lieux.error} />;
  const reservables = lieux.data.filter((l) => l.reservable);
  const lieu = reservables.find((l) => l.id === (lieuId ?? reservables[0]?.id));

  async function fetchOccupation(du, au) {
    if (!lieu) return [];
    const r = await api(`lieux/${lieu.id}/occupation?du=${du}&au=${au}`, { cache: `occupation_${lieu.id}_${du}` });
    return (r.ok ? r.data : []).map((o) => ({
      id: String(o.id), title: o.titre, start: o.debut, end: o.fin,
      extendedProps: { statut: o.statut === 'entrainement' ? 'planifiee' : o.statut },
    }));
  }

  async function demander(e) {
    e.preventDefault();
    const f = e.target;
    const r = await api('reservations', {
      method: 'POST',
      body: {
        lieu_id: lieu.id,
        debut: f.date.value + ' ' + f.debut.value + ':00',
        fin: f.date.value + ' ' + f.fin.value + ':00',
        motif: f.motif.value,
        nb_personnes: f.nb.value,
      },
      idempotencyKey: uuid(),
    });
    if (r.ok) {
      const c = r.data.conflits || {};
      let msg = 'Demande envoyée : le comité la traitera et vous serez notifié(e).';
      if (c.reservations || c.seances) msg += ` Attention : ${c.reservations || 0} réservation(s) et ${c.seances || 0} entraînement(s) chevauchent ce créneau.`;
      toast(msg);
      f.reset();
      reloadResas();
    } else toast(r.error, true);
  }

  async function annuler(id) {
    if (!confirm('Annuler cette réservation ?')) return;
    const r = await api('reservations/' + id, { method: 'DELETE' });
    if (r.ok) { toast('Réservation annulée.'); reloadResas(); } else toast(r.error, true);
  }

  const miennes = (mesResas.ok ? mesResas.data : []).filter((r) => !r.demandeur);

  return (
    <div>
      <h2 className="h4 mb-3">Réserver le club-house</h2>
      {reservables.length > 1 && (
        <select className="form-select mb-3" style={{ maxWidth: 320 }} value={lieu?.id || ''} onChange={(e) => setLieuId(parseInt(e.target.value, 10))}>
          {reservables.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
        </select>
      )}
      {!lieu && <p className="text-muted">Aucune ressource réservable.</p>}
      {lieu && (
        <>
          <div className="card mb-3"><div className="card-body">
            <h5>Occupation — {lieu.nom}</h5>
            <p className="text-muted small">Les créneaux occupés apparaissent sans identité du réservataire.</p>
            <CalendarView fetchEvents={fetchOccupation} />
          </div></div>
          <div className="card mb-3"><div className="card-body">
            <h5>Nouvelle demande</h5>
            <form onSubmit={demander} className="row g-2">
              <div className="col-md-3"><label className="form-label">Date *</label><input name="date" type="date" className="form-control" required /></div>
              <div className="col-md-2"><label className="form-label">Début *</label><input name="debut" type="time" className="form-control" required /></div>
              <div className="col-md-2"><label className="form-label">Fin *</label><input name="fin" type="time" className="form-control" required /></div>
              <div className="col-md-3"><label className="form-label">Motif *</label><input name="motif" className="form-control" required /></div>
              <div className="col-md-2"><label className="form-label">Personnes</label><input name="nb" type="number" min="1" className="form-control" /></div>
              <div className="col-12"><button className="btn btn-primary">Soumettre la demande</button></div>
            </form>
          </div></div>
          <h5>Mes réservations</h5>
          <div className="list-group">
            {miennes.length === 0 && <div className="text-muted p-2">Aucune réservation.</div>}
            {miennes.map((r) => (
              <div key={r.id} className="list-group-item d-flex justify-content-between align-items-center">
                <span>{r.lieu} — {fmtDateHeure(r.debut)} → {String(r.fin).slice(11, 16)}
                  <br /><small className="text-muted">{r.motif}{r.commentaire_comite ? ' · Comité : ' + r.commentaire_comite : ''}</small></span>
                <span className="d-flex align-items-center gap-2">
                  <Badge statut={r.statut} />
                  {['demandee', 'validee'].includes(r.statut) && (
                    <button className="btn btn-outline-danger btn-sm" onClick={() => annuler(r.id)}>Annuler</button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Validation des demandes par le comité (§ 7.2). */
export function ClubhouseValidation({ toast }) {
  const [annee, setAnnee] = useState('');
  const [state, reload] = useApi('reservations' + (annee ? '?annee=' + annee : ''), { deps: [annee] });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const demandes = state.data.filter((r) => r.statut === 'demandee');
  const anneeCourante = new Date().getFullYear();
  const annees = [];
  for (let a = anneeCourante + 1; a >= anneeCourante - 5; a--) annees.push(a);

  async function decider(id, statut) {
    if (statut === 'annulee' && !confirm('Annuler cette réservation ? Le demandeur sera notifié.')) return;
    const commentaire = ['refusee', 'annulee'].includes(statut) ? (prompt('Commentaire (facultatif) :') ?? '') : '';
    const messages = { validee: 'Réservation validée, demandeur notifié.', refusee: 'Réservation refusée, demandeur notifié.', annulee: 'Réservation annulée, demandeur notifié.' };
    const r = await api('reservations/' + id, { method: 'PATCH', body: { statut, commentaire } });
    if (r.ok) { toast(messages[statut]); reload(); }
    else toast(r.error, true);
  }

  async function modifier(r0) {
    const date = prompt('Nouvelle date (AAAA-MM-JJ) :', String(r0.debut).slice(0, 10));
    if (date === null) return;
    const formule = prompt('Formule (journee, demi_matin, demi_apres_midi — vide : heures inchangées) :', r0.formule || '');
    if (formule === null) return;
    const r = await api('reservations/' + r0.id, { method: 'PATCH', body: { date, formule } });
    if (r.ok) { toast('Réservation modifiée (prix recalculé selon la formule).'); reload(); } else toast(r.error, true);
  }

  async function supprimer(id) {
    if (!confirm('Supprimer définitivement cette réservation ?')) return;
    const r = await api('reservations/' + id + '?supprimer=1', { method: 'DELETE' });
    if (r.ok) { toast('Réservation supprimée.'); reload(); } else toast(r.error, true);
  }

  async function creer(e) {
    e.preventDefault();
    const f = e.target;
    const r = await api('reservations/publique', {
      method: 'POST',
      body: {
        date: f.date.value, formule: f.formule.value,
        prenom: f.prenom.value, nom: f.nom.value, email: f.email.value,
        telephone: f.telephone.value, adresse: f.adresse.value || '—',
        motif: f.motif.value, cle: f.cle.value,
      },
    });
    if (r.ok) { toast('Réservation créée (en attente de validation).'); f.reset(); reload(); }
    else toast(r.error, true);
  }

  return (
    <div>
      <h2 className="h4 mb-2">Gestion des réservations</h2>
      <Intro>Validez ou refusez les demandes de réservation du club-house.
        Un conflit avec une activité du club ou une location validée est bloquant.</Intro>
      <details className="card mb-3">
        <summary className="card-header" style={{ cursor: 'pointer' }}>+ Créer une réservation</summary>
        <form onSubmit={creer} className="card-body row g-2">
          <div className="col-md-3"><label className="form-label">Date *</label><input name="date" type="date" className="form-control" required /></div>
          <div className="col-md-3"><label className="form-label">Formule *</label>
            <select name="formule" className="form-select"><option value="journee">Journée</option><option value="demi_matin">Demi-journée (matin)</option><option value="demi_apres_midi">Demi-journée (après-midi)</option></select></div>
          <div className="col-md-3"><label className="form-label">Prénom *</label><input name="prenom" className="form-control" required /></div>
          <div className="col-md-3"><label className="form-label">Nom *</label><input name="nom" className="form-control" required /></div>
          <div className="col-md-4"><label className="form-label">Courriel *</label><input name="email" type="email" className="form-control" required /></div>
          <div className="col-md-3"><label className="form-label">Téléphone</label><input name="telephone" className="form-control" /></div>
          <div className="col-md-5"><label className="form-label">Adresse</label><input name="adresse" className="form-control" /></div>
          <div className="col-md-6"><label className="form-label">Motif</label><input name="motif" className="form-control" /></div>
          <div className="col-md-6"><label className="form-label">Clé</label>
            <select name="cle" className="form-select"><option value="besoin">A besoin d'une clé</option><option value="possede">Possède une clé</option></select></div>
          <div className="col-12"><button className="btn btn-primary btn-sm">Créer la réservation</button></div>
        </form>
      </details>
      {demandes.length === 0 && <p className="text-muted">Aucune demande en attente.</p>}
      {demandes.map((r) => (
        <div key={r.id} className="card mb-2"><div className="card-body">
          <div>
            <strong>{r.lieu}</strong> — {capitaliser(fmtDateHeure(r.debut))} → {String(r.fin).slice(11, 16)}
            <br /><small className="text-muted">{r.demandeur} · {r.motif}{r.nb_personnes ? ` · ${r.nb_personnes} pers.` : ''}</small>
            {r.conflits && (r.conflits.reservations > 0 || r.conflits.seances > 0) && (
              <><br /><small className="text-danger">⚠ Conflits : {r.conflits.reservations} réservation(s), {r.conflits.seances} entraînement(s)</small></>
            )}
          </div>
          {/* Quatre actions de poids égal — un quart de la carte chacune. */}
          <div className="row g-2 mt-1">
            <div className="col-3 d-grid">
              <button className="btn btn-success btn-sm" onClick={() => decider(r.id, 'validee')}>✓ Valider</button>
            </div>
            <div className="col-3 d-grid">
              <button className="btn btn-outline-danger btn-sm" onClick={() => decider(r.id, 'refusee')}>✗ Refuser</button>
            </div>
            <div className="col-3 d-grid">
              <button className="btn btn-info btn-sm" onClick={() => modifier(r)}>Modifier</button>
            </div>
            <div className="col-3 d-grid">
              <button className="btn btn-danger btn-sm d-flex align-items-center justify-content-center"
                title="Supprimer" aria-label="Supprimer" onClick={() => supprimer(r.id)}><IconeCorbeille /></button>
            </div>
          </div>
        </div></div>
      ))}
      <div className="d-flex justify-content-between align-items-center mt-4 mb-2 flex-wrap gap-2">
        <h5 className="mb-0">Toutes les réservations</h5>
        <select className="form-select form-select-sm" style={{ maxWidth: 160 }} value={annee} onChange={(e) => setAnnee(e.target.value)}>
          <option value="">Toutes les années</option>
          {annees.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div className="list-group">
        {state.data.filter((r) => r.statut !== 'demandee').map((r) => (
          <div key={r.id} className="list-group-item">
            <div>{r.lieu} — {capitaliser(fmtDateHeure(r.debut))}<br /><small className="text-muted">{r.demandeur}</small></div>
            {/* Statut compris, quatre colonnes de largeur égale. */}
            <div className="row g-2 mt-1 align-items-center">
              <div className="col-3 text-center"><Badge statut={r.statut} /></div>
              <div className="col-3 d-grid">
                {r.statut === 'validee'
                  ? <button className="btn btn-info btn-sm" onClick={() => modifier(r)}>Modifier</button>
                  : <button className="btn btn-info btn-sm" disabled>Modifier</button>}
              </div>
              <div className="col-3 d-grid">
                {['validee', 'demandee'].includes(r.statut)
                  ? <button className="btn btn-secondary btn-sm" onClick={() => decider(r.id, 'annulee')}>Annuler</button>
                  : <button className="btn btn-secondary btn-sm" disabled>Annuler</button>}
              </div>
              <div className="col-3 d-grid">
                <button className="btn btn-danger btn-sm d-flex align-items-center justify-content-center"
                  title="Supprimer" aria-label="Supprimer" onClick={() => supprimer(r.id)}><IconeCorbeille /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
